import { useMemo } from "react";
import Modal from "@/components/base/Modal";
import type { Client, Receivable, Sale } from "@/types";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";
import {
  buildClientTopProducts,
  receivableBalance,
  receivableMatchesClient,
  receivablePaid,
} from "@/utils/accounting";
import { printClientFicha } from "@/utils/clientFicha";

interface ClientDetailModalProps {
  open: boolean;
  client: Client | null;
  sales: Sale[];
  receivables: Receivable[];
  onClose: () => void;
  onPay: (r: Receivable) => void;
  onNewFiado: (clientName: string) => void;
}

// Ficha del cliente: datos de contacto, historial de compras línea por línea y,
// si el nombre coincide con una cuenta por cobrar, su saldo y abonos.
const ClientDetailModal = ({
  open,
  client,
  sales,
  receivables,
  onClose,
  onPay,
  onNewFiado,
}: ClientDetailModalProps) => {
  const ownSales = useMemo(() => {
    if (!client) return [];
    return sales
      .filter(
        (s) =>
          (client.id && s.clientId === client.id) ||
          (!!client.name &&
            s.clientName.trim().toLowerCase() === client.name.trim().toLowerCase())
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [client, sales]);

  const clientReceivables = useMemo(() => {
    if (!client) return [];
    return receivables.filter((r) => receivableMatchesClient(r.clientName, client.name));
  }, [client, receivables]);

  const topProducts = useMemo(() => buildClientTopProducts(ownSales).slice(0, 6), [ownSales]);

  if (!client) return null;

  const totalBilled = ownSales.reduce((a, s) => a + s.total, 0);
  const totalBalance = clientReceivables.reduce((a, r) => a + receivableBalance(r), 0);

  const location =
    [client.neighborhood, client.city, client.department, client.address]
      .filter(Boolean)
      .join(" · ") || "Sin ubicación";

  const stats = [
    { icon: "ri-shopping-bag-3-line", label: "Compras", value: formatNumber(ownSales.length), bubble: "bg-accent-500/20 text-accent-200" },
    { icon: "ri-money-dollar-circle-line", label: "Facturado", value: formatCOP(totalBilled), bubble: "bg-primary-500/25 text-primary-100" },
    { icon: "ri-hourglass-line", label: "Saldo cartera", value: formatCOP(totalBalance), bubble: "bg-secondary-500/25 text-secondary-200" },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={client.name}
      icon="ri-user-3-line"
      size="lg"
      footer={
        <>
          <button
            onClick={() => printClientFicha({ client, sales: ownSales, receivables: clientReceivables })}
            className="px-4 py-2.5 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            <i className="ri-printer-line" /> Imprimir / PDF
          </button>
          <button
            onClick={() => onNewFiado(client.name)}
            className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            <i className="ri-add-line" /> Registrar fiado
          </button>
        </>
      }
    >
      {/* Datos de contacto */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Cédula</p>
          <p className="text-sm font-semibold text-foreground-950">{client.cedula || "Sin cédula"}</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Celular</p>
          <p className="text-sm font-semibold text-foreground-950">{client.phone || "—"}</p>
        </div>
        <div className="sm:col-span-2 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Ubicación</p>
          <p className="text-sm font-semibold text-foreground-950 break-words">{location}</p>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2.5">
            <span className={`w-8 h-8 rounded-md flex items-center justify-center ${s.bubble}`}>
              <i className={`${s.icon} text-base`} />
            </span>
            <p className="text-[10px] text-foreground-500 mt-2">{s.label}</p>
            <p className="text-sm font-bold text-foreground-950 break-words">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Productos que más compra */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-star-smile-line text-accent-200" />
          <h4 className="text-sm font-bold text-foreground-950">Productos que más compra</h4>
          <span className="text-[11px] text-foreground-500">para ofrecerle en la próxima venta</span>
        </div>

        {topProducts.length === 0 ? (
          <p className="text-xs text-foreground-500 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-3">
            Aún no hay compras para analizar. Cuando registre ventas verás aquí sus productos
            preferidos.
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {topProducts.map((p, idx) => (
              <li
                key={p.productName}
                className="flex items-center gap-3 rounded-md border border-background-300/40 bg-background-200/30 px-3 py-2.5"
              >
                <span
                  className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    idx === 0
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-background-300/50 text-foreground-700"
                  }`}
                >
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-950 truncate" title={p.productName}>
                    {p.productName}
                  </p>
                  <p className="text-[11px] text-foreground-500">
                    {formatNumber(p.qty)} und · {formatNumber(p.purchases)}{" "}
                    {p.purchases === 1 ? "compra" : "compras"}
                  </p>
                </div>
                <span className="shrink-0 text-xs font-bold text-foreground-950">
                  {formatCOP(p.total)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Historial de compras */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-history-line text-accent-200" />
          <h4 className="text-sm font-bold text-foreground-950">Historial de compras</h4>
          <span className="text-[11px] text-foreground-500">({ownSales.length})</span>
        </div>

        {ownSales.length === 0 ? (
          <p className="text-xs text-foreground-500 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-3">
            Este cliente todavía no tiene compras registradas.
          </p>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-md border border-background-300/40">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-background-300/40">
                    <th className="text-left font-semibold px-3 py-2.5">Fecha</th>
                    <th className="text-left font-semibold px-3 py-2.5">Producto</th>
                    <th className="text-center font-semibold px-3 py-2.5">Cant.</th>
                    <th className="text-right font-semibold px-3 py-2.5">Total</th>
                    <th className="text-center font-semibold px-3 py-2.5">Canal</th>
                  </tr>
                </thead>
                <tbody>
                  {ownSales.map((s) => (
                    <tr key={s.id} className="border-b border-background-300/20 last:border-0">
                      <td className="px-3 py-2.5 text-foreground-700 whitespace-nowrap">{formatDate(s.date)}</td>
                      <td className="px-3 py-2.5 text-foreground-950">
                        <span className="block max-w-[260px] truncate" title={s.productName}>
                          {s.productName}
                        </span>
                        {s.historical && (
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-secondary-500/20 text-secondary-100">
                            Histórico
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center text-foreground-700">{s.qty}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-foreground-950">{formatCOP(s.total)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-500/15 text-accent-200">
                          {s.origin}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="md:hidden space-y-2">
              {ownSales.map((s) => (
                <li key={s.id} className="rounded-md border border-background-300/40 px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground-950 break-words">{s.productName}</p>
                    <span className="shrink-0 text-sm font-bold text-foreground-950">{formatCOP(s.total)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-foreground-500">
                    <span>{formatDate(s.date)}</span>
                    <span>· {s.qty} und</span>
                    <span>· {s.origin}</span>
                    {s.historical && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-secondary-500/20 text-secondary-100">
                        Histórico
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Cartera asociada */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-wallet-3-line text-accent-200" />
          <h4 className="text-sm font-bold text-foreground-950">Cartera / Por cobrar</h4>
        </div>

        {clientReceivables.length === 0 ? (
          <p className="text-xs text-foreground-500 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-3">
            Sin saldos por cobrar asociados a este cliente.
          </p>
        ) : (
          <div className="space-y-3">
            {clientReceivables.map((r) => {
              const paid = receivablePaid(r);
              const balance = receivableBalance(r);
              const settled = balance <= 0;
              return (
                <div key={r.id} className="rounded-md border border-background-300/40 overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 bg-background-200/40">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-950 break-words">{r.productName}</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5">{formatDate(r.date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-[10px] text-foreground-500">Saldo</p>
                        <p className="text-sm font-bold text-foreground-950">{formatCOP(balance)}</p>
                      </div>
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                          settled ? "bg-accent-500/20 text-accent-200" : "bg-secondary-500/20 text-secondary-100"
                        }`}
                      >
                        {settled ? "Pagado" : "Pendiente"}
                      </span>
                      <button
                        onClick={() => onPay(r)}
                        disabled={settled}
                        className={`px-3 py-1.5 rounded-md text-[11px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${
                          settled
                            ? "text-foreground-500/40 cursor-not-allowed"
                            : "border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 cursor-pointer"
                        }`}
                      >
                        <i className="ri-hand-coin-line" /> {settled ? "Pagado" : "Abonar"}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[11px]">
                    <div>
                      <p className="text-foreground-500">Valor inicial</p>
                      <p className="font-semibold text-foreground-800">{formatCOP(r.initialValue)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-500">Abonado</p>
                      <p className="font-semibold text-accent-200">{formatCOP(paid)}</p>
                    </div>
                    <div>
                      <p className="text-foreground-500">Abonos</p>
                      <p className="font-semibold text-foreground-800">{formatNumber(r.payments.length)}</p>
                    </div>
                  </div>

                  {r.payments.length > 0 && (
                    <ul className="divide-y divide-background-300/30 border-t border-background-300/30">
                      {r.payments.map((p, idx) => (
                        <li
                          key={`${idx}-${p.date}-${p.amount}`}
                          className="flex items-center justify-between px-3 py-2"
                        >
                          <span className="text-[11px] text-foreground-600">
                            Abono #{idx + 1} · {p.date ? formatDate(p.date) : "Sin fecha"}
                          </span>
                          <span className="text-xs font-semibold text-accent-200">{formatCOP(p.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClientDetailModal;